/**
 * RoleUploadPanel.jsx — RC 1.62 Phase B.
 *
 * Owns the candidate-dossier upload affordances that previously lived
 * inside `RoleCandidatesPanel`. Single + bulk upload modes; the same
 * validation, the same toasts, the same backend contracts.
 *
 * STRICT PHASE B BOUNDARY:
 *   • This component does NOT introduce any new backend route.
 *   • It does NOT change the upload contracts (PDF/DOCX 10MB, ZIP
 *     bulk, hash dedupe per role).
 *   • The only new behaviour is calling `onUploadComplete(kind)`
 *     after a SUCCESSFUL upload — single = non-duplicate parsed
 *     success; bulk = terminal batch with at least one parsed row.
 *     Duplicate / failed paths do NOT fire the callback (so the
 *     operator stays on the Upload tab to decide).
 *
 * Props:
 *   • roleId, roleTitle             — required.
 *   • onUploadComplete?(kind)       — optional; called once per
 *                                     successful single OR terminal
 *                                     bulk run with kind ∈
 *                                     {'single','bulk'}.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, Layers } from 'lucide-react';

import { apiFetch } from '@/lib/api';
import RoleBulkUploadPanel from './RoleBulkUploadPanel';

const MAX_BYTES   = 10 * 1024 * 1024;
const ACCEPTED_EXT = ['.pdf', '.docx'];

export default function RoleUploadPanel({
  roleId, roleTitle, onUploadComplete, onReviewFlaggedIntegrity,
}) {
  const [mode, setMode] = useState('single');  // 'single' | 'bulk'
  const [busy, setBusy] = useState(false);
  const fileInputRef    = useRef(null);

  const onPickFile = () => fileInputRef.current?.click();

  const onFile = useCallback(async (ev) => {
    const file = ev.target?.files?.[0];
    if (!file) return;
    ev.target.value = '';  // allow re-uploading the same file later

    // Client-side gates — preserved verbatim from RoleCandidatesPanel.
    const lower = file.name.toLowerCase();
    if (!ACCEPTED_EXT.some((ext) => lower.endsWith(ext))) {
      toast.error('Only PDF and DOCX files are supported.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Max ${MAX_BYTES / (1024 * 1024)} MB.`);
      return;
    }

    setBusy(true);
    let body;
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      try {
        body = await apiFetch(
          `/api/employer/v1/roles/${roleId}/candidates/upload`,
          { method: 'POST', body: form },
        );
      } catch (err) {
        const code    = err?.data?.detail?.code || err?.data?.code || err?.code || 'UPLOAD_FAILED';
        const message = err?.data?.detail?.message || err?.data?.message || err?.message || 'Upload failed.';
        toast.error(`${code}: ${message}`);
        return;
      }
      if (body?.duplicate) {
        // Duplicate path — stay on Upload tab so the operator can decide.
        toast.warning('This Candidate Dossier is already uploaded to this role.');
      } else if (body?.parse_status === 'parsed') {
        toast.success(`Uploaded · parsed ${body.original_filename}`);
        // Success path — auto-switch to Candidates tab.
        if (typeof onUploadComplete === 'function') {
          try { onUploadComplete('single'); } catch { /* never block UI on hand-off */ }
        }
      } else {
        toast.error(`Parse failed: ${body?.parse_status || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }, [roleId, onUploadComplete]);

  const onBulkTerminalSuccess = useCallback(() => {
    if (typeof onUploadComplete === 'function') {
      try { onUploadComplete('bulk'); } catch { /* never block UI on hand-off */ }
    }
  }, [onUploadComplete]);

  return (
    <div className="space-y-5" data-testid="role-upload-panel">
      {/* Mode toggle — Single vs Bulk Intake */}
      <div
        className="inline-flex rounded-md border border-ndor-edge p-0.5 bg-ndor-bg w-full"
        role="tablist"
        aria-label="Upload mode"
        data-testid="role-candidates-upload-mode"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'single'}
          onClick={() => setMode('single')}
          className={`flex-1 text-[11px] uppercase tracking-wider px-2 py-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
            mode === 'single'
              ? 'bg-ndor-ink text-ndor-surface'
              : 'text-slate-200 hover:text-ndor-ink'
          }`}
          data-testid="role-candidates-mode-single"
        >
          Single dossier
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'bulk'}
          onClick={() => setMode('bulk')}
          className={`flex-1 text-[11px] uppercase tracking-wider px-2 py-1.5 rounded flex items-center justify-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
            mode === 'bulk'
              ? 'bg-ndor-ink text-ndor-surface'
              : 'text-slate-200 hover:text-ndor-ink'
          }`}
          data-testid="role-candidates-mode-bulk"
        >
          <Layers className="h-3.5 w-3.5" />
          Bulk dossiers
        </button>
      </div>

      {mode === 'single' && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={onFile}
            data-testid="role-candidate-upload-input"
          />
          <Button
            onClick={onPickFile}
            disabled={busy}
            size="sm"
            className="w-full bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted focus-visible:ring-2 focus-visible:ring-blue-400"
            data-testid="role-candidate-upload-trigger"
          >
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading…</>
            ) : (
              <><Upload className="mr-2 h-4 w-4" />Upload Candidate Dossier</>
            )}
          </Button>
          <p className="text-xs text-slate-200 mt-2 leading-relaxed">
            PDF or DOCX · max 10 MB · one Candidate Dossier per candidate.
            Combine CV (required) plus optional cover letter, supporting
            statement or notes in a single file. Certificates, portfolios
            and reference letters are not part of an NDORFlow dossier.
          </p>
        </div>
      )}

      {mode === 'bulk' && (
        <RoleBulkUploadPanel
          roleId={roleId}
          roleTitle={roleTitle}
          onTerminalSuccess={onBulkTerminalSuccess}
          onReviewFlaggedIntegrity={onReviewFlaggedIntegrity}
        />
      )}
    </div>
  );
}
